import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useAuth, getOwnerKey } from '../hooks/useAuth';

interface ListRef { token: string; title: string; saves: number; }

const AccountPage: React.FC = () => {
  const { user, login, register, logout, authHeaders } = useAuth();
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<ListRef[]>([]);
  const [saved, setSaved] = useState<ListRef[]>([]);

  useEffect(() => {
    fetch(`/api/account/lists?saverKey=${getOwnerKey()}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => { setCreated(d.created || []); setSaved(d.saved || []); })
      .catch(() => {});
  }, [user, authHeaders]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'login') await login(email, password);
      else await register(email, password, name);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-100">
      <SEO title="Your account" description="Your GoodCart account: saved lists and picks." url="https://goodcart.org/account" />
      <div className="container-custom py-10 max-w-lg">
        <h1 className="font-display text-3xl text-earth-900 mb-8">Your account</h1>

        {!user ? (
          <div className="bg-white rounded-2xl border border-earth-100 p-6 md:p-8">
            <div className="flex rounded-full border border-earth-200 p-0.5 mb-6" role="group" aria-label="Login or register">
              {(['login', 'register'] as const).map((m) => (
                <button key={m} type="button" onClick={() => setMode(m)}
                  aria-pressed={mode === m}
                  className={`flex-1 py-2 text-sm rounded-full transition-colors ${mode === m ? 'bg-sage-600 text-cream-50' : 'text-earth-600'}`}>
                  {m === 'login' ? 'Sign in' : 'Create account'}
                </button>
              ))}
            </div>
            <form onSubmit={submit} className="space-y-4">
              {mode === 'register' && (
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)"
                  className="w-full py-2.5 px-4 border border-earth-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sage-500" />
              )}
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
                autoComplete="email"
                className="w-full py-2.5 px-4 border border-earth-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sage-500" />
              <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Password (8+ characters)"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                className="w-full py-2.5 px-4 border border-earth-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sage-500" />
              {error && <p className="text-clay-600 text-sm" role="alert">{error}</p>}
              <button type="submit" disabled={busy} className="btn btn-primary w-full">
                {busy ? 'One moment…' : mode === 'login' ? 'Sign in' : 'Create account'}
              </button>
            </form>
            <p className="text-xs text-earth-500 mt-4">Accounts keep your lists attached to you across devices. Guest lists also work, keyed to this browser.</p>
          </div>
        ) : (
          <div className="space-y-8">
            <div className="bg-white rounded-2xl border border-earth-100 p-6 flex items-center justify-between">
              <div>
                <p className="font-medium text-earth-900">{user.name}</p>
                <p className="text-sm text-earth-500">{user.email}</p>
              </div>
              <button type="button" onClick={logout} className="btn btn-outline btn-sm">Sign out</button>
            </div>
          </div>
        )}

        {(created.length > 0 || saved.length > 0) && (
          <div className="mt-10 space-y-8">
            {created.length > 0 && (
              <section>
                <h2 className="font-display text-xl text-earth-900 mb-3">Lists you created</h2>
                <ul className="bg-white rounded-2xl border border-earth-100 divide-y divide-earth-100">
                  {created.map((l) => (
                    <li key={l.token}>
                      <Link to={`/lists/${l.token}`} className="flex items-center justify-between px-5 py-3.5 hover:bg-cream-50 transition-colors">
                        <span className="text-earth-800">{l.title}</span>
                        <span className="text-sm text-earth-500 tabular-nums">{l.saves} saves</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {saved.length > 0 && (
              <section>
                <h2 className="font-display text-xl text-earth-900 mb-3">Lists you saved</h2>
                <ul className="bg-white rounded-2xl border border-earth-100 divide-y divide-earth-100">
                  {saved.map((l) => (
                    <li key={l.token}>
                      <Link to={`/lists/${l.token}`} className="flex items-center justify-between px-5 py-3.5 hover:bg-cream-50 transition-colors">
                        <span className="text-earth-800">{l.title}</span>
                        <span className="text-sm text-earth-500 tabular-nums">{l.saves} saves</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountPage;
